import { createContext, useContext, useState, useCallback } from "react"
import axios from "axios"
import { BACKEND } from "../../../config/configEnvs"
import { toast } from "react-toastify"

/** Igual que `mailService`: cookies de sesión hacia Payload (necesario si el front y el API no son mismo origen). */
const payloadClient = axios.create({ withCredentials: true })

const TareasContext = createContext(null)

const buildWhereParams = (obj, prefix = "where") => {
  const params = []
  for (const [key, val] of Object.entries(obj)) {
    const fullKey = `${prefix}[${key}]`
    if (Array.isArray(val)) {
      val.forEach((v, i) => {
        if (v !== null && typeof v === "object") {
          params.push(...buildWhereParams(v, `${fullKey}[${i}]`))
        } else {
          params.push(`${fullKey}[${i}]=${encodeURIComponent(v)}`)
        }
      })
    } else if (val !== null && typeof val === "object") {
      params.push(...buildWhereParams(val, fullKey))
    } else {
      params.push(`${fullKey}=${encodeURIComponent(val)}`)
    }
  }
  return params
}

export const DEFAULT_STATUSES = [
  { id: "Por comenzar", name: "Por comenzar", color: "#6B7280" },
  { id: "En proceso",   name: "En proceso",   color: "#3B82F6" },
  { id: "Finalizado",   name: "Finalizado",   color: "#10B981" },
  { id: "Declinado",    name: "Declinado",    color: "#EF4444" },
  { id: "Incumplido",   name: "Incumplido",   color: "#F59E0B" },
]

export const PRIORITY_CONFIG = {
  Baja:    { color: "#6B7280", bg: "#F3F4F6" },
  Media:   { color: "#3B82F6", bg: "#EFF6FF" },
  Alta:    { color: "#F59E0B", bg: "#FFFBEB" },
  Urgente: { color: "#EF4444", bg: "#FEF2F2" },
}

export const TareasProvider = ({ children }) => {
  const [tasks, setTasks]               = useState([])
  const [workers, setWorkers]           = useState([])
  const [estadosTareas, setEstadosTareas] = useState([])
  const [projects, setProjects]         = useState([])
  const [projectsLoading, setProjectsLoading] = useState(false)
  const [selectedProject, setSelectedProject]  = useState(null)
  const [loading, setLoading]           = useState(false)
  const [totalPages, setTotalPages]     = useState(0)
  const [totalDocs, setTotalDocs]       = useState(0)
  const [page, setPage]                 = useState(1)
  const [limit, setLimit]               = useState(15)

  // Vista activa: "tabla" | "kanban" | "lista" | "calendario"
  const [activeView, setActiveView]     = useState("paginas")

  const DEFAULT_STATUS_IDS = [
    "69aad9a3a8d95067530d4b1a",
    "69aad956a8d95067530d4ae4",
    "69aad907a8d95067530d4ac0",
  ]

  // Filtros
  const [filters, setFilters] = useState({
    search: "",
    status: [],
    worker: "",
    priority: [],
    categories: [],
    projectTagIds: [],
    dateFrom: "",
    dateTo: "",
  })

  // Tarea abierta en el drawer de detalle
  const [selectedTask, setSelectedTask] = useState(null)
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false)

  // Modal de creación
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [createModalPreselectedStatus, setCreateModalPreselectedStatus] = useState(null)
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false)

  const openCreateModal = (preselectedStatus = null) => {
    setCreateModalPreselectedStatus(preselectedStatus)
    setCreateModalOpen(true)
  }

  const closeCreateModal = () => {
    setCreateModalOpen(false)
    setCreateModalPreselectedStatus(null)
  }

  const openTask = (task) => {
    setSelectedTask(task)
    setDetailDrawerOpen(true)
  }

  const closeDetailDrawer = () => {
    setDetailDrawerOpen(false)
    setSelectedTask(null)
  }

  const fetchProjects = useCallback(async () => {
    setProjectsLoading(true)
    try {
      const res = await payloadClient.get(`${BACKEND}/api/proyectosAdmin?limit=0&depth=0&where[archived][equals]=false`)
      setProjects(res.data.docs || [])
    } catch {
      toast.error("Error cargando proyectos")
    } finally {
      setProjectsLoading(false)
    }
  }, [])

  const fetchWorkers = useCallback(async () => {
    try {
      const res = await payloadClient.get(`${BACKEND}/api/trabajadores?where[activo][equals]=true&limit=0`)
      setWorkers(res.data.docs)
    } catch {
      toast.error("Error cargando trabajadores")
    }
  }, [])

  const fetchEstadosTareas = useCallback(async (projectId) => {
    try {
      let whereStr
      if (projectId) {
        whereStr = `where[or][0][proyecto][exists]=false&where[or][1][proyecto][equals]=${encodeURIComponent(projectId)}`
      } else {
        whereStr = `where[proyecto][exists]=false`
      }
      const url = `${BACKEND}/api/estadosTareas?limit=0&sort=order&${whereStr}`
      const res = await payloadClient.get(url)
      setEstadosTareas(res.data.docs || [])
    } catch {
      toast.error("Error cargando estados")
    }
  }, [])

  // Mapeo nombre -> id para el campo status (relationship)
  const getStatusId = useCallback((statusNameOrId) => {
    if (!statusNameOrId) return null
    const estado = estadosTareas.find(e => e.id === statusNameOrId || e.name === statusNameOrId)
    return estado?.id ?? (estadosTareas.length === 0 ? statusNameOrId : null)
  }, [estadosTareas])

  const getTaskStatusValue = useCallback((task) => {
    const s = task?.status
    if (!s) return null
    if (typeof s === "object") return s.name ?? s.id
    const estado = estadosTareas.find(e => e.id === s)
    return estado?.name ?? s
  }, [estadosTareas])

  const getTaskStatusId = useCallback((task) => {
    const s = task?.status
    if (!s) return null
    if (typeof s === "object") return s.id ?? null
    // Si es string, puede ser ID o nombre: resolver a ID para que coincida con columnas
    const estado = estadosTareas.find(e => e.id === s || e.name === s)
    return estado?.id ?? s
  }, [estadosTareas])

  const getTaskStatusColor = useCallback((task) => {
    const s = task?.status
    if (!s) return "#9CA3AF"
    const estado = typeof s === "object"
      ? estadosTareas.find(e => e.id === s.id) || s
      : estadosTareas.find(e => e.id === s)
    return estado?.color ?? "#9CA3AF"
  }, [estadosTareas])

  const fetchTasks = useCallback(async (overridePage, overrideLimit, overrideFilters) => {
    if (!selectedProject?.id) {
      setTasks([])
      setTotalDocs(0)
      return
    }
    setLoading(true)
    try {
      const p = overridePage ?? page
      const l = overrideLimit ?? limit
      const f = overrideFilters ?? filters

      const where = { proyecto: { equals: selectedProject.id } }

      if (f.worker && f.worker !== "Todos") {
        where.assignees = { equals: f.worker }
      }
      if (f.status?.length > 0) {
        const ids = f.status.map(s => getStatusId(s)).filter(Boolean)
        if (ids.length === 1) {
          where.status = { equals: ids[0] }
        } else if (ids.length > 1) {
          where.status = { in: ids }
        }
      } else if (f.categories?.length > 0) {
        const idsByCategory = estadosTareas
          .filter(e => f.categories.includes(e.category || "No iniciado"))
          .map(e => e.id)
        if (idsByCategory.length > 0) {
          where.status = idsByCategory.length === 1
            ? { equals: idsByCategory[0] }
            : { in: idsByCategory }
        }
      }
      if (f.search) {
        where.task = { like: f.search }
      }
      if (f.priority?.length > 0) {
        where.priority = f.priority.length === 1
          ? { equals: f.priority[0] }
          : { in: f.priority }
      }
      if (f.dateFrom) {
        where.limitDate = { ...(where.limitDate || {}), greater_than_equal: f.dateFrom }
      }
      if (f.dateTo) {
        where.limitDate = { ...(where.limitDate || {}), less_than_equal: f.dateTo }
      }
      if (f.projectTagIds?.length > 0) {
        where["projectTags.tagId"] = f.projectTagIds.length === 1
          ? { equals: f.projectTagIds[0] }
          : { in: f.projectTagIds }
      }

      const whereParams = buildWhereParams(where).join("&")
      console.log("[fetchTasks] whereParams:", whereParams)
      const url = `${BACKEND}/api/tareasAdministracion?limit=${l}&page=${p}&depth=2&${whereParams}`

      const response = await payloadClient.get(url)

      setTasks(response.data.docs)
      setTotalPages(response.data.totalPages)
      setTotalDocs(response.data.totalDocs)
    } catch (error) {
      toast.error("Error ")
    } finally {
      setLoading(false)
    }
  }, [page, limit, filters, selectedProject?.id, getStatusId, estadosTareas])

  const createProject = async (data) => {
    try {
      const res = await payloadClient.post(`${BACKEND}/api/proyectosAdmin`, data)
      toast.success("Proyecto creado con éxito")
      setProjects(prev => [...prev, res.data.doc])
      await fetchProjects()
    } catch (error) {
      toast.error("Error al crear el proyecto")
      throw error
    }
  }

  const updateProject = async (projectId, data) => {
    try {
      const res = await payloadClient.put(`${BACKEND}/api/proyectosAdmin/${projectId}`, data)
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, ...res.data.doc } : p))
      if (selectedProject?.id === projectId) setSelectedProject(prev => ({ ...prev, ...res.data.doc }))
      toast.success("Proyecto actualizado")
      return res.data.doc
    } catch {
      toast.error("Error al actualizar el proyecto")
    }
  }

  const archiveProject = async (projectId) => {
    try {
      await payloadClient.put(`${BACKEND}/api/proyectosAdmin/${projectId}`, { archived: true })
      setProjects(prev => prev.filter(p => p.id !== projectId))
      if (selectedProject?.id === projectId) setSelectedProject(null)
      toast.success("Proyecto archivado")
    } catch {
      toast.error("Error al archivar el proyecto")
    }
  }

  const unarchiveProject = async (projectId) => {
    try {
      const res = await payloadClient.put(`${BACKEND}/api/proyectosAdmin/${projectId}`, { archived: false })
      setProjects(prev => [...prev, res.data.doc])
      toast.success("Proyecto desarchivado")
      return res.data.doc
    } catch {
      toast.error("Error al desarchivar el proyecto")
    }
  }

  const fetchArchivedProjects = async () => {
    try {
      const res = await payloadClient.get(`${BACKEND}/api/proyectosAdmin?limit=0&depth=0&where[archived][equals]=true`)
      return res.data.docs || []
    } catch {
      toast.error("Error cargando proyectos archivados")
      return []
    }
  }

  const deleteProject = async (projectId) => {
    try {
      const confirmed = window.confirm("¿Seguro deseas eliminar este proyecto? Se eliminarán todas sus tareas.")
      if (!confirmed) return false
      await payloadClient.delete(`${BACKEND}/api/proyectosAdmin/${projectId}`)
      setProjects(prev => prev.filter(p => p.id !== projectId))
      if (selectedProject?.id === projectId) setSelectedProject(null)
      toast.success("Proyecto eliminado")
      return true
    } catch {
      toast.error("Error al eliminar el proyecto")
      return false
    }
  }

  const createTask = async (data) => {
    try {
      await payloadClient.post(`${BACKEND}/api/tareasAdministracion`, data)
      toast.success("Tarea creada con éxito")
      fetchTasks()
    } catch (error) {
      console.log(error)
      toast.error("Error al crear la tarea")
      throw error
    }
  }

  const updateTask = async (taskId, data) => {
    try {
      const res = await payloadClient.put(`${BACKEND}/api/tareasAdministracion/${taskId}`, data)
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...data } : t))
      if (selectedTask?.id === taskId) {
        setSelectedTask(prev => ({ ...prev, ...data }))
      }
      return res.data
    } catch (error) {
      console.log(error)
      toast.error("Error al actualizar la tarea")
      throw error
    }
  }

  const deleteTask = async (taskId) => {
    try {
      const confirmed = window.confirm("¿Seguro deseas eliminar esta tarea?")
      if (!confirmed) return false

      const taskResponse = await payloadClient.delete(`${BACKEND}/api/tareasAdministracion/${taskId}`)
      const audioIds = taskResponse.data.audios || []
      for (const audio of audioIds) {
        await payloadClient.delete(`${BACKEND}/api/audiosTareasAdmin/${audio.id}`)
      }

      setTasks(prev => prev.filter(t => t.id !== taskId))
      setTotalDocs(prev => prev - 1)
      toast.success("Tarea eliminada")
      return true
    } catch {
      toast.error("Error al eliminar la tarea")
      return false
    }
  }

  const createEstadoTarea = async ({ name, color, category, order }) => {
    try {
      const payload = { name, color, category, order: order ?? estadosTareas.length }
      if (selectedProject?.id) payload.proyecto = selectedProject.id
      const res = await payloadClient.post(`${BACKEND}/api/estadosTareas`, payload)
      const nuevo = res.data.doc
      setEstadosTareas(prev => [...prev, nuevo].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)))
      toast.success("Estado creado")
      return nuevo
    } catch {
      toast.error("Error al crear el estado")
      throw new Error()
    }
  }

  const deleteEstadoTarea = async (estadoId) => {
    try {
      await payloadClient.delete(`${BACKEND}/api/estadosTareas/${estadoId}`)
      setEstadosTareas(prev => prev.filter(e => e.id !== estadoId))
      toast.success("Estado eliminado")
    } catch {
      toast.error("Error al eliminar el estado")
    }
  }

  const uploadAudio = async (file) => {
    const formData = new FormData()
    formData.append("file", file)
    const res = await payloadClient.post(`${BACKEND}/api/audiosTareasAdmin`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
    return res.data.doc.id
  }

  const applyFilters = (newFilters) => {
    setFilters(newFilters)
    setPage(1)
    fetchTasks(1, limit, newFilters)
  }

  const handlePageChange = (_, value) => {
    setPage(value)
    fetchTasks(value, limit, filters)
  }

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit)
    setPage(1)
    fetchTasks(1, newLimit, filters)
  }

  return (
    <TareasContext.Provider value={{
      tasks, setTasks,
      workers,
      projects,
      projectsLoading,
      selectedProject,
      setSelectedProject,
      loading,
      totalPages, totalDocs,
      page, limit,
      activeView, setActiveView,
      filters, applyFilters,
      selectedTask,
      detailDrawerOpen,
      openTask,
      closeDetailDrawer,
      createModalOpen, setCreateModalOpen, openCreateModal, closeCreateModal,
      createModalPreselectedStatus,
      createProjectModalOpen, setCreateProjectModalOpen,
      createProject, updateProject, archiveProject, unarchiveProject, deleteProject, fetchArchivedProjects,
      fetchTasks,
      fetchWorkers,
      fetchEstadosTareas,
      fetchProjects,
      estadosTareas,
      getStatusId,
      getTaskStatusValue,
      getTaskStatusId,
      getTaskStatusColor,
      createTask,
      updateTask,
      deleteTask,
      uploadAudio,
      createEstadoTarea,
      deleteEstadoTarea,
      handlePageChange,
      handleLimitChange,
    }}>
      {children}
    </TareasContext.Provider>
  )
}

export const useTareas = () => {
  const ctx = useContext(TareasContext)
  if (!ctx) throw new Error("useTareas debe usarse dentro de TareasProvider")
  return ctx
}
